// src/services/warehouseCountryService.ts
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../../firebase/config';

export interface CountryOption {
  code: string;
  name: string;
}

export interface StateOption {
  id: string;
  name: string;
}

export interface CountryWithStates {
  code: string;
  name: string;
  states: StateOption[];
}

const SHIPPING_COLLECTION = 'shippingRates';

export class WarehouseCountryService {
  // Cache to avoid multiple fetches
  private static countriesCache: CountryWithStates[] | null = null;
  private static cacheTimestamp: number = 0;
  private static CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Get all countries with their states from shipping rates collection
   */
  static async getAllCountriesWithStates(): Promise<CountryWithStates[]> {
    try {
      // Return cached data if available and not expired
      const now = Date.now();
      if (
        this.countriesCache && 
        this.cacheTimestamp && 
        (now - this.cacheTimestamp) < this.CACHE_DURATION
      ) {
        return this.countriesCache;
      }

      const q = query(
        collection(db, SHIPPING_COLLECTION),
        orderBy('name', 'asc')
      );
      
      const snapshot = await getDocs(q);
      
      const countries: CountryWithStates[] = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          code: data.code,
          name: data.name,
          states: (data.states || []).map((state: any) => ({
            id: state.id,
            name: state.name,
          })),
        };
      });

      // Update cache
      this.countriesCache = countries;
      this.cacheTimestamp = now;

      return countries;
    } catch (error) {
      console.error('Error fetching countries and states:', error);
      throw error;
    }
  }

  /**
   * Get all countries (without states)
   */
  static async getAllCountries(): Promise<CountryOption[]> {
    try {
      const countriesWithStates = await this.getAllCountriesWithStates();
      return countriesWithStates.map(country => ({
        code: country.code,
        name: country.name,
      }));
    } catch (error) {
      console.error('Error fetching countries:', error);
      throw error;
    }
  }

  /**
   * Get states by country name
   */
  static async getStatesByCountryName(countryName: string): Promise<StateOption[]> {
    try {
      const countriesWithStates = await this.getAllCountriesWithStates();
      const country = countriesWithStates.find(c => c.name === countryName);
      return country?.states || [];
    } catch (error) {
      console.error('Error fetching states:', error);
      throw error;
    }
  }

  /**
   * Get states by country code
   */
  static async getStatesByCountryCode(countryCode: string): Promise<StateOption[]> {
    try {
      const countriesWithStates = await this.getAllCountriesWithStates();
      const country = countriesWithStates.find(c => c.code === countryCode);
      return country?.states || [];
    } catch (error) {
      console.error('Error fetching states:', error);
      throw error;
    }
  }

  /**
   * Clear cache (useful when shipping rates are updated)
   */
  static clearCache(): void {
    this.countriesCache = null;
    this.cacheTimestamp = 0;
  }
}